import {
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  MenuItem,
  Modal,
  Select,
  Typography,
  List,
  FormControl,
  ClickAwayListener,
  Checkbox,
  ListItem,
  ListItemText,
  ListItemButton,
  CircularProgress,
  Dialog,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import DragDropLists from 'common/shared-ui/src/components/DragDropLists/DragDropLists';
import ClearIcon from 'common/shared-ui/src/icons/ClearIcon';
import DeleteIcon from 'common/shared-ui/src/icons/DeleteIcon';
import { getTranslatedText } from 'common/utils/getTranslatedText';

import {
  getResplanSettings,
  updateResplanSettings,
} from '../../../../packages/common/api';
import { useTranslation } from '../../../../packages/common/utils/getLocalString';
import {
  clearResplanTableRows,
  setResplanTableRows,
  setStopLoadingRespanTableBySocket,
} from '../../app/redux/reducers/userSlice';

// Original class: modalRoot
const StyledModalRoot = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-container': {
    padding: '84px 0 20px 0',
  },

  '& .MuiPaper-root.MuiDialog-paper': {
    overflow: 'visible',
    padding: '20px',
    maxWidth: '600px',
    backgroundColor: '#F1F2F8',
    borderRadius: '16px',
    margin: 0,
    maxHeight: '100%',
  },

  modalPaper: {
    padding: theme.spacing(2.5, 5),
    overflowY: 'auto',
  },
}));
// Original class: blockWrapper
const StyledBlockWrapper = styled(Grid)(theme => ({
  flexWrap: 'nowrap!important',
  overflowY: 'auto',

  '&::-webkit-scrollbar': {
    height: '6px',
    width: '6px',
  },

  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#7174AC',
    borderRadius: '5px',
  },
}));

// Original class: formControlWrap
const StyledFormControlWrap = styled(Grid)(theme => ({
  '& .MuiGrid-item': {
    padding: '0 !important',
  },

  '& .MuiGrid-root': {
    margin: '0 !important',
  },
}));
// Original class: formControl
const StyledFormControl = styled(FormControl)(({ theme }) => ({
  margin: '0 !important',
  padding: '0 !important',
  display: 'flex',
  width: '100%',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  backgroundColor: '#ffffff',
  borderRadius: theme.spacing(4),

  '& .MuiSelect-icon': {
    display: 'none',
  },

  '& .MuiInputBase-formControl': {
    width: '100%',
    height: 'auto !important',
  },

  '& .MuiSelect-select': {
    padding: '3px !important',
  },
}));
// Original class: optionsGroupMenu
const StyledOptionsGroupMenu = styled(Box)(theme => ({
  '&.MuiPopover-root': {
    zIndex: 3000,
  },

  '& .MuiPaper-root': {
    backgroundColor: '#ffffff',

    '& ul': {
      padding: 0,
    },
  },

  anchorOrigin: {
    vertical: 'top',
    horizontal: 'center',
  },

  transformOrigin: {
    vertical: 'top',
    horizontal: 'center',
  },

  style: {
    maxHeight: 500,
  },
}));
// Original class: chips
const StyledChips = styled(Box)(theme => ({
  display: 'flex',
  flexWrap: 'wrap',
  color: '#212346;',
  gap: '5px',

  '& .MuiChip-root': {
    margin: 2,
    backgroundColor: '#FFF',
    border: '1px solid #E4E4EF',
    borderRadius: '14px',
  },

  '& .MuiChip-deleteIcon': {
    height: '10px',
    marginRight: '8px !important',
    marginLeft: '4px !important',
  },
}));
// Original class: chip
const StyledChip = styled(Chip)(theme => ({
  '&.MuiChip-root': {
    height: '30px',
    margin: '3px 4px',
    fontSize: '12px',
    display: 'flex',
    border: '1px solid #E4E4EF',
    borderRadius: '14px',
    '& .MuiChip-label': {
      padding: '0 8px',
    },
    '& .MuiChip-deleteIcon': {
      marginRight: '5px',
      position: 'static',
      height: '10px !important',
    },
  },
}));
// Original class: menuItemCheckbox
const StyledMenuItemCheckbox = styled(Checkbox)(theme => ({
  color: 'red',

  '& .MuiSvgIcon-root': {
    width: 16,
    height: 16,
    color: '#3448FF',
  },
}));
// Original class: dragWrap
const StyledDragWrap = styled(Grid)(theme => ({
  width: '100% !important',
}));

const ResourcePlanModal = ({
  open,
  handleClose,
  setRefreshKey,
  setFetchedQuery,
  query,
  setQuery,
  setIsLoading,
}) => {
  const getLocalString = useTranslation();
  const [filters, setFilters] = React.useState([]);
  const [fields, setFields] = React.useState([]);
  const [isModalLoading, setIsModalLoading] = useState(true);
  const [modalSessionParams, setModalSessionParams] = useState({
    availableFields: [],
    selectedFields: [],
  });
  const dispatch = useDispatch();

  const modifyQuery = initialQuery => {
    const params = initialQuery.split('$$');

    return params
      .filter(
        param =>
          param.startsWith('resourcePlan.group_by;') ||
          param.startsWith('resourcePlan.date_from;') ||
          param.startsWith('resourcePlan.date_to;'),
      )
      .join('$$');
  };

  const fetchSettingsData = async () => {
    try {
      const response = await getResplanSettings();
      const modifiedFields = response.fields.map(item => {
        if (
          item.fieldId === 'resourcePlan.project' ||
          item.fieldId === 'resourcePlan.user' ||
          item.fieldId === 'resourcePlan.monthly_distribution'
        ) {
          return { ...item, isChecked: true };
        }
        return item;
      });

      setFilters(response.filters);
      setFields(modifiedFields);
      setIsModalLoading(false);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    fetchSettingsData();
  }, []);

  const handleSelectChange = event => {
    const selectedFieldIds = event.target.value;
    setFilters(prevFilters =>
      prevFilters.map(filter =>
        selectedFieldIds.includes(filter.fieldId)
          ? { ...filter, isChecked: true }
          : { ...filter, isChecked: false },
      ),
    );
  };

  const handleRemoveFilter = useCallback(fieldId => {
    setFilters(prevFilters => {
      return prevFilters.map(filter =>
        filter.fieldId === fieldId ? { ...filter, isChecked: false } : filter,
      );
    });
  }, []);

  useEffect(() => {
    if (fields.length > 0) {
      const nonDraggables = fields.filter(
        field =>
          ['resourcePlan.project', 'resourcePlan.user'].includes(
            field.fieldId,
          ) && field.isChecked,
      );
      const restOfTheFields = fields.filter(
        field =>
          !['resourcePlan.project', 'resourcePlan.user'].includes(
            field.fieldId,
          ) && field.isChecked,
      );

      setModalSessionParams({
        availableFields: fields.filter(field => !field.isChecked),
        selectedFields: [
          ...nonDraggables,
          ...restOfTheFields.sort((a, b) => a.position - b.position),
        ],
      });
    }
  }, [fields]);

  const handleClearSettings = async () => {
    fetchSettingsData();
  };
  const handleSaveSettings = async () => {
    const { selectedFields } = modalSessionParams;

    const payload = {
      settings: {
        filters: filters
          .filter(item => item.isChecked)
          .map(item => ({
            instance: item.instance,
            field: item.field,
          })),
        fields: selectedFields.map(item => ({
          instance: item.instance,
          field: item.field,
        })),
      },
    };
    await updateResplanSettings(payload);
    const modifiedQuery = modifyQuery(query);
    dispatch(setResplanTableRows({ fields: [], rows: [] }));
    dispatch(clearResplanTableRows());
    dispatch(setStopLoadingRespanTableBySocket(true));
    setFetchedQuery(modifiedQuery);
    setQuery(modifiedQuery);
    setRefreshKey(prevKey => prevKey + 1);
    handleClose();
  };

  return (
    <>
      {!isModalLoading ? (
        <StyledModalRoot
          fullWidth={true}
          open={open}
          onClose={handleClose}
          aria-labelledby='modal-modal-title'
          aria-describedby='modal-modal-description'
        >
          <Grid
            container
            wrap='nowrap'
            sx={{
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
            }}
          >
            <Typography variant='h1'>
              {getLocalString('buttons', 'params')}
            </Typography>
            <IconButton disableRipple onClick={handleClose}>
              <Box
                sx={{
                  width: 24,
                  height: 24,
                }}
              >
                <ClearIcon width={24} height={24} viewBox='0 0 24 24' />
              </Box>
            </IconButton>
          </Grid>
          <StyledBlockWrapper
            container
            spacing={5}
            sx={{
              position: 'relative',
            }}
          >
            <Grid size={3}>
              <Typography variant='h5'>
                {getLocalString('labels', 'filter')}
              </Typography>
            </Grid>
            <Grid size={'grow'}>
              <StyledFormControl>
                <Select
                  id='filters'
                  multiple
                  value={filters
                    .filter(filter => filter.isChecked)
                    .map(filter => filter.fieldId)}
                  MenuProps={{
                    slots: { list: StyledOptionsGroupMenu },
                  }}
                  onChange={handleSelectChange}
                  renderValue={selected => {
                    return (
                      <StyledChips>
                        {selected
                          .filter(
                            item =>
                              (item != 'resourcePlan.group_by') &
                              (item != 'resourcePlan.date_to') &
                              (item != 'resourcePlan.date_from'),
                          )
                          .map(value => {
                            const filter = filters.find(
                              filter => filter.fieldId === value,
                            );
                            return (
                              <StyledChip
                                key={filter.field}
                                label={filter.fieldName}
                                clickable
                                disabled={filter.field == 'status'}
                                deleteIcon={
                                  <DeleteIcon
                                    width={10}
                                    height={10}
                                    viewBox='0 0 10 10'
                                    onMouseDown={event =>
                                      event.stopPropagation()
                                    }
                                  />
                                }
                                onDelete={() =>
                                  handleRemoveFilter(filter.fieldId)
                                }
                              />
                            );
                          })}
                      </StyledChips>
                    );
                  }}
                >
                  {filters
                    .filter(
                      item =>
                        (item.fieldId != 'resourcePlan.group_by') &
                        (item.fieldId != 'resourcePlan.date_to') &
                        (item.fieldId != 'resourcePlan.date_from'),
                    )
                    .sort((a, b) => b.isChecked - a.isChecked)
                    .map(filter => (
                      <MenuItem key={filter.field} value={filter.fieldId}>
                        <StyledMenuItemCheckbox
                          checked={filter.isChecked}
                          disabled={filter.field == 'status'}
                        />
                        <ListItemText primary={filter.fieldName} />
                      </MenuItem>
                    ))}
                </Select>
              </StyledFormControl>
            </Grid>
          </StyledBlockWrapper>
          <StyledDragWrap
            container
            spacing={5}
            sx={{
              marginTop: 5,
              marginLeft: 0,
              justifyContent: 'space-between',
              display: 'flex',
              position: 'relative',
            }}
          >
            <DragDropLists
              firstListOptions={modalSessionParams.availableFields}
              secondListOptions={modalSessionParams.selectedFields}
              setParentState={setModalSessionParams}
              getOptionLabel={option => `${option.fieldId};${option.fieldName}`}
              nonDraggableIds={['resourcePlan.project', 'resourcePlan.user']}
            />
          </StyledDragWrap>
          <Grid
            container
            sx={{
              justifyContent: 'center',
              marginTop: 5,
            }}
          >
            <Button
              disableRipple
              variant='cutGreyPrimaryBig'
              onClick={handleClearSettings}
            >
              <Typography
                variant='h5'
                sx={{
                  fontWeight: 700,
                }}
              >
                {getLocalString('buttons', 'clear')}
              </Typography>
            </Button>
            <Box
              sx={{
                marginLeft: 1,
              }}
            >
              <Button
                disableRipple
                variant='cutBlueSecondary'
                onClick={handleSaveSettings}
              >
                <Typography
                  variant='h5'
                  sx={{
                    fontWeight: 700,
                  }}
                >
                  {getLocalString('buttons', 'save')}
                </Typography>
              </Button>
            </Box>
          </Grid>
        </StyledModalRoot>
      ) : (
        <Grid
          container
          sx={{
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100vh',
          }}
        >
          <CircularProgress color='secondary' />
        </Grid>
      )}
    </>
  );
};

export default ResourcePlanModal;
